import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { Notification } from '@/api/types/notification-types';
import { NOTIFICATIONS_PAGE_SIZE } from '@/api/notificationsApi';
import type { DiscussionGroup } from './types';

interface DiscussionsState {
  ownBlogGroups: Record<string, DiscussionGroup>;
  otherBlogGroups: Record<string, DiscussionGroup>;
  mentionGroups: Record<string, DiscussionGroup>;
  nextApiPage: number;
  hasMoreFromApi: boolean;
}

const initialState: DiscussionsState = {
  ownBlogGroups: {},
  otherBlogGroups: {},
  mentionGroups: {},
  nextApiPage: 1,
  hasMoreFromApi: true
};

const discussionsSlice = createSlice({
  name: 'discussions',
  initialState,
  reducers: {
    appendNotifications(
      state,
      action: PayloadAction<{ notifications: Notification[]; myBlogSlug: string; activeProfileId: string }>
    ) {
      const { notifications, myBlogSlug, activeProfileId } = action.payload;

      for (const n of notifications) {
        if (typeof n.entry !== 'object') continue;

        let targetMap: Record<string, DiscussionGroup>;
        if (n.type === 'mention') {
          targetMap = state.mentionGroups;
        } else if (n.type === 'comment') {
          targetMap = n.blog === activeProfileId ? state.ownBlogGroups : state.otherBlogGroups;
        } else {
          continue;
        }

        const entry = n.entry;
        const entryId = entry.id;

        if (!targetMap[entryId]) {
          const entryProfile = entry.profile ?? n.source;
          targetMap[entryId] = {
            entryId,
            blog: n.blog,
            entry: entryProfile ? { ...entry, profile: entryProfile } : entry,
            blogSlug: entryProfile?.blogSlug ?? myBlogSlug,
            notifications: [],
            hasUnread: false,
            unreadCount: 0,
            latestAt: ''
          };
        }

        const group = targetMap[entryId];
        group.notifications.push(n);

        if (n.state === 'new') {
          group.hasUnread = true;
          group.unreadCount++;
        }

        const d = n.comments?.createdAt ?? '';
        if (d > group.latestAt) {
          group.latestAt = d;
        }
      }

      state.nextApiPage += 1;
      if (notifications.length < NOTIFICATIONS_PAGE_SIZE) {
        state.hasMoreFromApi = false;
      }
    },
    resetAll() {
      return initialState;
    }
  }
});

export const discussionsActions = discussionsSlice.actions;
export const discussionsReducer = discussionsSlice.reducer;
